import React, { HTMLAttributes } from 'react';
import classNames from 'classnames';
import { FormLabel } from '..';
import * as styles from '../Form.module.scss';
import { useUniqueID } from 'hooks/useAccessibleName';
import {
  Input,
  Select,
  Text,
  TextArea,
  Checkbox,
  RadioButton,
  RadioGroup,
  ComboBox,
  MultiSelectComboBox,
} from 'components/core';
import { Inline } from 'components/layout';

type Props = {
  error?: string | JSX.Element | null;
  helperText?: string | JSX.Element;
  actions?: React.ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const isFormInput = (child: React.ReactElement<any>): boolean => {
  return (
    child.type === Input ||
    child.type === Select ||
    child.type === TextArea ||
    child.type === Checkbox ||
    child.type === RadioButton ||
    child.type === RadioGroup ||
    child.type === ComboBox ||
    child.type === MultiSelectComboBox
  );
};

const FormError = ({
  error,
  id,
}: {
  id: string;
  error?: string | React.ReactElement<typeof Text> | null;
}) => {
  if (!error) {
    return null;
  }

  if (React.isValidElement(error)) {
    return <div id={id}>{error}</div>;
  }

  return (
    <Text id={id} variant="danger" size="xs">
      {error}
    </Text>
  );
};

const FormHelper = ({
  helperText,
  id,
}: {
  id: string;
  helperText?: string | React.ReactElement<typeof Text>;
}) => {
  if (!helperText) {
    return null;
  }

  if (React.isValidElement(helperText)) {
    return <div id={id}>{helperText}</div>;
  }

  return (
    <Text id={id} variant="weak" size="xs">
      {helperText}
    </Text>
  );
};

const FormControl = ({
  className,
  children,
  error,
  helperText,
  actions,
  ...props
}: Props) => {
  const generatedInputId = useUniqueID();
  const actionsId = useUniqueID();
  const errorId = useUniqueID();
  const helperId = useUniqueID();

  const childArray = React.Children.toArray(children);

  // Use the input's own id when it has one, so the label still points at it
  const inputChild = childArray.find(
    (child): child is React.ReactElement =>
      React.isValidElement(child) && isFormInput(child)
  );
  const inputId = inputChild?.props.id || generatedInputId;

  const childTypes = childArray.map((child) =>
    React.isValidElement(child) ? child.type : null
  );
  const hasRadioGroup = childTypes.includes(RadioGroup);
  const hasFormLabel = childTypes.includes(FormLabel);

  // A RadioGroup is named via aria-labelledby, so its label has nothing to point at
  const labelFor = hasRadioGroup ? undefined : inputId;

  // RadioGroup actions describe the whole group, so they sit beside its label
  const hasActions = !!actions;
  const actionsBesideLabel = hasActions && hasRadioGroup && hasFormLabel;
  const actionsBesideInput = hasActions && !actionsBesideLabel;

  // Link the input to its error message
  const errorAriaProps: React.AriaAttributes = error
    ? { 'aria-errormessage': errorId, 'aria-invalid': 'true' }
    : {};
  const describedByIds = [helperText && helperId, hasActions && actionsId];

  const actionsNode = hasActions ? (
    <Inline id={actionsId} alignX="left" spacing={1}>
      {actions}
    </Inline>
  ) : null;

  const renderInput = (child: React.ReactElement) => {
    const isRadioGroup = child.type === RadioGroup;
    const inputProps: Record<string, unknown> = {
      ...errorAriaProps,
      isError: !!error || child.props.isError,
    };

    // Keep the input's own description ids first
    const describedBy = [child.props['aria-describedby'], ...describedByIds]
      .filter(Boolean)
      .join(' ');
    if (describedBy) inputProps['aria-describedby'] = describedBy;

    // A RadioGroup passes its props on to every radio, so a generated id
    // would end up duplicated on each of them
    if (!isRadioGroup) {
      inputProps.id = child.props.id || inputId;
    }

    const clonedInput = React.cloneElement(child, inputProps);

    if (!actionsBesideInput) return clonedInput;

    return (
      <Inline
        alignX="left"
        // Align with the first radio, not the middle of the group
        alignY={isRadioGroup ? 'top' : 'center'}
        spacing={1}
        className={styles.FormControlActionsRow}
      >
        {clonedInput}
        {actionsNode}
      </Inline>
    );
  };

  const renderLabel = (child: React.ReactElement) => {
    const clonedLabel = React.cloneElement(child, {
      htmlFor: child.props.htmlFor || labelFor,
    } as React.HTMLAttributes<HTMLLabelElement>);

    if (!actionsBesideLabel) return clonedLabel;

    // Siblings, so the actions aren't part of the group's name
    return (
      <Inline alignX="left" spacing={1}>
        {clonedLabel}
        {actionsNode}
      </Inline>
    );
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if (isFormInput(child)) return renderInput(child);
    if (child.type === FormLabel) return renderLabel(child);
    return child;
  });

  return (
    <div className={classNames(styles.FormControl, className)} {...props}>
      {childrenWithProps}
      <div role="alert" className={styles.FormErrorAlert}>
        <FormError id={errorId} error={error} />
      </div>
      <FormHelper id={helperId} helperText={helperText} />
    </div>
  );
};

export { FormControl };
